import React, { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import callApi from "../helpers/callApi";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import DeleteForever from "@mui/icons-material/DeleteForever";
import UndoIcon from "@mui/icons-material/Undo";
import RouterLinkButton from "./RouterLinkButton";
import { renderReleasedIcon } from "./pipelineStageStatus";

const ScriptVersionsTable = (params) => {
  const { auth, scriptName, scriptsEndpoint } = params;
  const [scriptVersions, setScriptVersions] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // { scriptName, version } to confirm deletion
  const [deleting, setDeleting] = useState(false);

  const renderReleased = (params) => renderReleasedIcon(params.value);

  const renderVersionActions = (params) => {
    if (params.value?.releasable) {
      return (
        // release, test, delete
        <Stack direction="row" spacing={1}>
          <Tooltip title="Release this version">
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => releaseVersion(scriptName, params.row.version)}
            >
              <RocketLaunchIcon fontSize="small" />
            </Button>
          </Tooltip>
          <RouterLinkButton
            to={`/convos/${scriptName}/${params.row.version}`}
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<RecordVoiceOverIcon />}
            primary="Talk"
          />
          <Tooltip title="Delete unreleased version">
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => setDeleteTarget({ scriptName, version: params.row.version })}
            >
              <DeleteForever fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
      );
    } else {
      return (
        // unrelease, test
        <Stack direction="row" spacing={1}>
          <Tooltip title="Unrelease this version">
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => unReleaseVersion(scriptName, params.row.version)}
            >
              <UndoIcon fontSize="small" />
            </Button>
          </Tooltip>
          <RouterLinkButton
            to={`/convos/${scriptName}/${params.row.version}`}
            size="small"
            variant="contained"
            color="primary"
            startIcon={<RecordVoiceOverIcon />}
            primary="Talk"
          />
        </Stack>
      );
    }
  };

  const versionColumns = [
    { field: "id", headerName: "ID", width: 150 },
    { field: "modified_at", headerName: "Released At", width: 200 },
    { field: "version", headerName: "Version", width: 100 },
    {
      field: "released",
      headerName: "Released?",
      width: 110,
      renderCell: renderReleased,
      align: "center",
      headerAlign: "center",
      display: "flex",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 280,
      renderCell: renderVersionActions,
      sortable: false,
      display: "flex",
    },
  ];

  useEffect(() => {
    getVersions();
  }, [scriptName]);

  const getVersions = async () => {
    try {
      const versionsResponse = await callApi(
        `${scriptsEndpoint}/${scriptName}`,
        "GET",
        undefined,
        auth.user?.access_token,
      );
      for (const r of versionsResponse) {
        r.actions = {
          releasable: !r.released,
        };
      }
      setScriptVersions(versionsResponse);
    } catch (err) {
      // surpress errors about 404 not found because will always while first version of a script is being imported
      if (err?.status !== 404) {
        enqueueSnackbar(err.message, { variant: "error" });
      }
    }
  };

  const releaseVersion = async (scriptName, scriptVersion) => {
    try {
      const body = { release: true };
      await callApi(`${scriptsEndpoint}/${scriptName}/${scriptVersion}`, "PUT", body, auth.user?.access_token);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    await getVersions();
  };

  const unReleaseVersion = async (scriptName, scriptVersion) => {
    try {
      const body = { release: false };
      await callApi(`${scriptsEndpoint}/${scriptName}/${scriptVersion}`, "PUT", body, auth.user?.access_token);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    await getVersions();
  };

  const handleDeleteConfirm = async () => {
    if (deleteTarget === null) return;
    setDeleting(true);
    try {
      await callApi(
        `${scriptsEndpoint}/${deleteTarget.scriptName}/${deleteTarget.version}`,
        "DELETE",
        undefined,
        auth.user?.access_token,
      );
      setDeleteTarget(null);
      await getVersions();
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="h6">Versions</Typography>
      <DataGrid
        rows={scriptVersions}
        columns={versionColumns}
        autoHeight
        density="comfortable"
        initialState={{
          sorting: {
            sortModel: [{ field: "version", sort: "desc" }],
          },
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        sx={{ border: 0 }}
      />

      <Dialog open={deleteTarget !== null} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete unreleased version?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete{" "}
            <Box component="span" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
              {deleteTarget?.scriptName} v{deleteTarget?.version}
            </Box>
            . This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ScriptVersionsTable;
